import {
  ArrowRight,
  Check,
  Eye,
  Linkedin,
  type LucideIcon,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/shadcn/utils';
import { enterStep } from '@/modules/careers/careers';
import type { JobApplicationRequestApi } from '@/modules/careers/types';
import { Button } from '@/modules/ui/components/button/Button';

interface JobApplicationSubmittedProps {
  /** What the applicant sent, as submitted (already trimmed). */
  application: JobApplicationRequestApi;
  jobTitle: string;
  companyName: string;
  /** The Careers Page, for applicants who want to look at other roles. */
  allRolesPath: string;
}

/** The Apply card after a successful submission: what was sent and what happens next. */
export function JobApplicationSubmitted({
  application,
  jobTitle,
  companyName,
  allRolesPath,
}: JobApplicationSubmittedProps) {
  const steps: Step[] = [
    { icon: Check, title: 'Application sent', detail: 'Just now', done: true },
    {
      icon: Eye,
      title: 'The team reviews your profile',
      detail: application.motivation
        ? 'Based on your LinkedIn profile and your answer'
        : 'Based on your LinkedIn profile',
    },
    {
      icon: Linkedin,
      title: 'They reach out on LinkedIn',
      detail: 'Keep an eye on your LinkedIn messages',
    },
  ];

  return (
    <div className="flex flex-col gap-7" data-enter role="status">
      <span className="bg-semantic-560/10 text-semantic-560 grid size-12 place-items-center rounded-full transition-[opacity,scale] duration-300 ease-(--ease-out-strong) starting:scale-75 starting:opacity-0 motion-reduce:starting:scale-100">
        <Check aria-hidden className="size-6" />
      </span>
      <div {...enterStep(1)} className="flex flex-col gap-2">
        <h2 className="text-2xl font-semibold tracking-[-0.025em]">
          {application.firstName
            ? `Thanks for applying, ${application.firstName}!`
            : 'Thanks for applying!'}
        </h2>
        <p className="text-muted-foreground text-[15px] leading-relaxed">
          Your application for {jobTitle} at {companyName} is in. Here’s what
          happens next.
        </p>
      </div>
      <ol>
        {steps.map((step, index) => (
          <StepItem
            enterAt={2 + index}
            isLast={index === steps.length - 1}
            key={step.title}
            step={step}
          />
        ))}
      </ol>
      <div
        {...enterStep(5)}
        className="bg-sidebar flex flex-col gap-2.5 rounded-xl border p-4 text-[13px]"
      >
        <p className="text-muted-foreground text-[11px] font-semibold tracking-[0.05em] uppercase">
          You sent
        </p>
        <dl className="flex flex-col gap-2.5">
          <SentRow
            label="Name"
            value={[application.firstName, application.lastName].join(' ')}
          />
          <SentRow label="Email" value={application.email} />
          <SentRow label="LinkedIn" value={application.linkedInProfileUrl} />
        </dl>
      </div>
      <div {...enterStep(6)} className="flex flex-col">
        <Button
          asChild
          className="h-11 rounded-xl font-semibold [&>svg]:transition-transform [&>svg]:duration-150 [&>svg]:ease-out hover:[&>svg]:translate-x-0.5"
          variant="outline"
        >
          <Link to={allRolesPath}>
            See other open roles
            <ArrowRight aria-hidden />
          </Link>
        </Button>
      </div>
    </div>
  );
}

interface Step {
  icon: LucideIcon;
  title: string;
  detail: string;
  done?: boolean;
}

function StepItem({
  step,
  isLast,
  enterAt,
}: {
  step: Step;
  isLast: boolean;
  enterAt: number;
}) {
  const Icon = step.icon;

  return (
    <li {...enterStep(enterAt)} className="flex gap-3.5">
      <div className="flex w-7 shrink-0 flex-col items-center">
        <span
          className={cn(
            'grid size-7 shrink-0 place-items-center rounded-full',
            step.done
              ? 'bg-semantic-560 text-white'
              : 'bg-muted text-muted-foreground',
          )}
        >
          <Icon aria-hidden className="size-3.5" />
        </span>
        {!isLast && <span aria-hidden className="bg-border w-px flex-1" />}
      </div>
      <div className={cn('flex flex-col gap-0.5 pt-1', !isLast && 'pb-5')}>
        <p className="text-[15px] font-medium">{step.title}</p>
        <p className="text-muted-foreground text-[13px]">{step.detail}</p>
      </div>
    </li>
  );
}

function SentRow({ label, value }: { label: string; value: string | null }) {
  if (!value?.trim()) return null;

  return (
    <div className="flex justify-between gap-4">
      <dt className="text-muted-foreground shrink-0">{label}</dt>
      <dd className="min-w-0 truncate font-medium">{value}</dd>
    </div>
  );
}
